import type { TicketStatus, TicketStatusFilter, TicketType } from '@nadoutong/contracts';
import { isTicketStatus } from '@nadoutong/contracts';
import { PlatformApiError } from '../../api/client.ts';
import { localizeApiError } from '../../api/errorMessages.ts';
import type { I18nKey } from '../../i18n/index.ts';
import type { ChipTone } from '../../ui/index.ts';
import type { Translate } from '../usage/shared.tsx';

/**
 * Shared ticket vocabulary for `/tickets` and the notification page:
 * label keys, status→tone mapping, URL param narrowing, and the error
 * describers for every branch the #116 contract names. Kept out of the
 * components so the branches stay unit-testable without a DOM.
 */

export const TICKET_TYPE_LABEL_KEYS: Readonly<Record<TicketType, I18nKey>> = {
  billing: 'tickets.create.type.billing',
  usage: 'tickets.create.type.usage',
  api: 'tickets.create.type.api',
  model: 'tickets.create.type.model',
  other: 'tickets.create.type.other',
};

export const TICKET_STATUS_LABEL_KEYS: Readonly<Record<TicketStatusFilter, I18nKey>> = {
  all: 'tickets.status.all',
  open: 'tickets.status.open',
  processing: 'tickets.status.processing',
  resolved: 'tickets.status.resolved',
  closed: 'tickets.status.closed',
};

/**
 * Field Notes has no blue, so the desktop's amber/blue/green/gray maps
 * onto the token families as warn/rust/ok/neutral — rust as the one
 * sanctioned "active state" accent for work in progress.
 */
export const TICKET_STATUS_CHIP_TONES: Readonly<Record<TicketStatus, ChipTone>> = {
  open: 'warn',
  processing: 'rust',
  resolved: 'ok',
  closed: 'neutral',
};

/**
 * URL params are user-editable; narrow instead of trusting, so a value
 * the API would 400 on collapses to `all` rather than being forwarded.
 */
export function parseTicketStatusFilter(value: string | null): TicketStatusFilter {
  return value !== null && isTicketStatus(value) ? value : 'all';
}

/** `?page=` reader: positive integer or 1. */
export function parseTicketPage(value: string | null): number {
  if (value === null) {
    return 1;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
}

/**
 * Create-ticket error branches, in contract order of specificity:
 *
 *   1. 503 with attachments = the OSS-degraded window (staging/prod
 *      before object storage is wired). NOT a submission failure — the
 *      copy must steer toward a text-only ticket.
 *   2. 429 TICKET_LIMIT_EXCEEDED — the org holds ≥20 open+processing.
 *   3. 429 (RATE_LIMITED) — 5 tickets per hour per account.
 */
export function describeCreateTicketError(
  error: unknown,
  hadAttachments: boolean,
  t: Translate,
): string {
  if (error instanceof PlatformApiError) {
    if (error.status === 503 && hadAttachments) {
      return t('tickets.create.toast.attachments_unavailable');
    }
    if (error.code === 'TICKET_LIMIT_EXCEEDED') {
      return t('tickets.create.toast.limit');
    }
    if (error.status === 429) {
      return t('tickets.create.toast.rate_limited');
    }
    return localizeApiError(error, t);
  }
  return t('tickets.create.toast.failed');
}

export interface ReplyErrorPresentation {
  readonly message: string;
  /**
   * 409 TICKET_CLOSED and 404 TICKET_NOT_FOUND both mean the local row
   * is stale — refresh the list so the composer disables (or the row
   * disappears) according to what the server actually holds.
   */
  readonly refreshTickets: boolean;
}

export function describeReplyError(error: unknown, t: Translate): ReplyErrorPresentation {
  if (error instanceof PlatformApiError) {
    if (error.code === 'TICKET_CLOSED' || error.status === 409) {
      return { message: t('tickets.replies.toast.closed'), refreshTickets: true };
    }
    if (error.code === 'TICKET_NOT_FOUND') {
      return { message: t('tickets.error.not_found'), refreshTickets: true };
    }
    if (error.status === 429) {
      return { message: t('tickets.replies.toast.rate_limited'), refreshTickets: false };
    }
    return { message: localizeApiError(error, t), refreshTickets: false };
  }
  return { message: t('tickets.replies.toast.failed'), refreshTickets: false };
}

/**
 * Read-path errors (list / replies). `TICKET_NOT_FOUND` is deliberately
 * indistinguishable between "does not exist" and "not yours" — the copy
 * says exactly that and no more.
 */
export function describeTicketLoadError(error: unknown, t: Translate): string {
  if (error instanceof PlatformApiError) {
    if (error.code === 'TICKET_NOT_FOUND') {
      return t('tickets.error.not_found');
    }
    return localizeApiError(error, t);
  }
  return t('tickets.list.error');
}
